import { first_truthy, normalize_text } from './view_case_details_core_helpers';

const AVATAR_CLASSES = ['is-green', 'is-amber', 'is-purple', 'is-blue'];
const ASSIGNED_AVATAR_CLASSES = ['bg-success', 'case-details-avatar-amber', 'bg-purple text-white', 'bg-primary'];

function full_name(first_name, last_name) {
  return [first_name, last_name].map(normalize_text).filter(Boolean).join(' ');
}

export function user_full_name(user = {}) {
  return normalize_text(first_truthy([
    user?.name,
    full_name(user?.first_name, user?.last_name),
    user?.email,
    user?.username,
    'Unnamed user',
  ]));
}

export function user_initials(name = '', email = '') {
  const source = normalize_text(name) || normalize_text(email);
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return source.slice(0, 2).toUpperCase() || 'U';

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function normalize_reassign_user(profile = {}, index = 0) {
  const user = profile?.user || profile || {};
  const role = user?.role || profile?.role || {};
  const role_name = reassign_role_name(role, user, profile);
  const role_id = Number(role?.id || user?.role_id || profile?.role_id || 0) || null;
  const user_id = Number(profile?.user_id || user?.id || profile?.id || 0) || null;
  const name = user_full_name(user);
  const email = normalize_text(user?.email || profile?.email);

  return {
    key: String(user_id || `${name}-${index}`),
    user_id,
    role_id,
    initials: user_initials(name, email),
    name,
    detail: [role_name, email].filter(Boolean).join(' - '),
    badge: role_name,
    badge_class: role_name.toLowerCase().includes('coordinator') ? 'is-coordinator' : 'is-available',
    avatar_class: AVATAR_CLASSES[index % AVATAR_CLASSES.length],
  };
}

function reassign_role_name(role, user, profile) {
  return normalize_text(first_truthy([
    role?.role,
    role?.name,
    user?.role_name,
    profile?.role_name,
    typeof role === 'string' ? role : '',
    'User',
  ]));
}

export function assigned_user_role_name(assignment = {}) {
  return normalize_text(first_truthy([
    assignment?.role_name,
    assignment?.role,
    assignment?.assigned_role,
    assignment?.assigned_profile_role?.role?.role,
  ]));
}

export function assigned_user_name(assignment = {}) {
  return normalize_text(first_truthy([
    assignment?.assigned_to_name,
    assignment?.name,
    assignment?.user_name,
    assignment?.assigned_to?.full_name,
    assignment?.assigned_to?.email,
    assignment?.assigned_to_email,
  ]));
}

export function assignment_summary_value(assignment = {}) {
  const name = assigned_user_name(assignment);
  const role_name = assigned_user_role_name(assignment);

  return [name, role_name].filter(Boolean).join(' - ') || 'Unassigned';
}

function assignment_identity_key(assignment = {}) {
  const user_id = assignment?.assigned_to_id || assignment?.user_id || assignment?.assigned_to?.id;
  const role_id = assignment?.assigned_profile_role_id || assignment?.role_id || assignment?.assigned_profile_role?.id;
  const role_name = assigned_user_role_name(assignment);
  const email = normalize_text(assignment?.assigned_to_email || assignment?.email);
  const name = assigned_user_name(assignment);

  return String([
    user_id || name || email || 'unknown-user',
    role_id || role_name || 'unknown-role',
  ].join(':'));
}

export function unique_assigned_users(assignments = []) {
  const seen = new Set();

  return assignments.filter((assignment) => {
    const key = assignment_identity_key(assignment);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function assignment_summary_list(assignments = []) {
  return unique_assigned_users(assignments)
    .map((assignment) => assignment_summary_value(assignment))
    .filter(Boolean)
    .join(', ');
}

export function assignment_matches_role(assignment = {}, role_fragment = '') {
  return assigned_user_role_name(assignment).toLowerCase().includes(role_fragment);
}

export function normalize_assigned_person(assignment = {}, index = 0) {
  const name = assigned_user_name(assignment) || 'Unassigned';
  const role_name = assigned_user_role_name(assignment) || 'Assigned user';
  const email = normalize_text(assignment?.assigned_to_email || assignment?.email);

  return {
    key: assignment?.id || assignment?.assigned_to_id || `${name}-${index}`,
    initials: user_initials(name, email),
    class: ASSIGNED_AVATAR_CLASSES[index % ASSIGNED_AVATAR_CLASSES.length],
    name,
    role: role_name,
  };
}
